import { HTMLAttributes, ReactElement, ReactNode, cloneElement, forwardRef, useMemo, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import {
  FloatingArrow,
  FloatingPortal,
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react'
import { getFloatingMotionProps } from '../Floating'
import { cn } from '@dashboard/ui'

interface Options {
  /**
   * Show an arrow pointing to the reference element.
   */
  arrow?: boolean
  /**
   * Placement of the tooltip near the trigger element.
   */
  placement?: Placement
  /**
   * Distance from trigger element.
   */
  distance?: number
  /**
   * Delay before the item appears and disappears, you can customize the stages individually.
   */
  delay?:
    | number
    | Partial<{
        open: number
        close: number
      }>
  /**
   * Disabling the operation of the tooltip.
   */
  disabled?: boolean
  /**
   * Display large text in the tooltip.
   */
  supportingText?: boolean
  /**
   * Will close the tooltip when the trigger element is clicked.
   */
  closeOnPress?: boolean
  tooltipBgColor?: string
}

interface TooltipProps extends HTMLAttributes<HTMLElement> {
  /**
   * Element, the trigger for the tooltip, must necessarily have a `forwardRef`.
   */
  children: ReactElement
  /**
   * Tooltip content, you can use a custom element.
   */
  message: ReactNode
  /**
   * Tooltip behavior settings.
   */
  options?: Options
}

const defaultOptions: Options = {
  arrow: true,
  distance: 12,
  delay: { open: 250, close: 0 },
  disabled: false,
  supportingText: false,
  closeOnPress: false,
  tooltipBgColor: '#000000CC',
}

export const Tooltip = forwardRef<HTMLElement, TooltipProps>(function Tooltip(
  { children, message, options: _options, ...rest },
  ref,
) {
  const arrowRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const options: Options = { ...defaultOptions, ..._options }

  const {
    refs,
    context,
    floatingStyles,
    placement: floatingPlacement,
  } = useFloating({
    open: isOpen,
    placement: options.placement,
    strategy: 'fixed',
    middleware: [
      shift({ padding: 4 }),
      offset(options.distance),
      flip(),
      options.arrow && arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
    onOpenChange: setIsOpen,
  })

  const { arrow: arrowData, shift: shiftData } = context.middlewareData

  const arrowStaticPosition = useMemo(
    () => ({
      left: (shiftData?.x ?? 0) > 0 ? (arrowData?.x ?? 0) - (shiftData?.x ?? 0) : (arrowData?.x ?? undefined),
      top: (shiftData?.y ?? 0) > 0 ? (arrowData?.y ?? 0) - (shiftData?.y ?? 0) : (arrowData?.y ?? undefined),
    }),
    [shiftData, arrowData],
  )

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      delay: options.delay,
      enabled: !options.disabled,
      handleClose: safePolygon({ requireIntent: true }),
    }),
    useDismiss(context, {
      referencePress: options.closeOnPress,
    }),
  ])

  const referenceMergedRef = useMergeRefs([refs.setReference, ref])

  if (!message) {
    return children
  }

  return (
    <>
      {cloneElement(children, {
        ...rest,
        ...getReferenceProps(),
        ref: referenceMergedRef,
        'data-open-tooltip': isOpen,
      })}

      <AnimatePresence>
        {!options.disabled && isOpen && (
          <FloatingPortal>
            <div className="z-[3100]" ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
              <div
                className={cn(
                  'max-w-[240px] rounded-md px-2 py-1 text-[12px] font-medium text-text-inverse shadow-md backdrop-blur',
                  options.supportingText ? 'px-1' : 'text-center',
                )}
                style={{ backgroundColor: options.tooltipBgColor }}
                {...getFloatingMotionProps(floatingPlacement)}
              >
                {options.arrow && (
                  <FloatingArrow
                    ref={arrowRef}
                    context={context}
                    tipRadius={3}
                    width={12}
                    height={6}
                    staticOffset={arrowStaticPosition.left || arrowStaticPosition.top || undefined}
                    fill={options.tooltipBgColor}
                  />
                )}
                {message}
              </div>
            </div>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  )
})
